import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import ical from 'ical.js';
import axios from 'axios';
import { fetchIcalAvailability, createReservation } from '../services/api';
import { Reservation, Season } from '../types';

const MAX_PEOPLE = 8;

const formatLocalDate = (d: Date): string => {
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

export const useReservation = () => {
    const { t } = useTranslation();
    const location = useLocation();

    // Check for state from navigation
    const initialState = location.state as {
        startDate?: string | Date;
        endDate?: string | Date;
        adults?: number;
        children?: number;
    } | null;

    const [dates, setDates] = useState<[Date | null, Date | null]>([
        initialState?.startDate ? new Date(initialState.startDate) : null,
        initialState?.endDate ? new Date(initialState.endDate) : null
    ]);
    const [blockedDates, setBlockedDates] = useState<Set<string>>(new Set());
    const [arrivalTime, setArrivalTime] = useState<string>('16:00');
    const [firstName, setFirstName] = useState<string>('');
    const [lastName, setLastName] = useState<string>('');
    const [email, setEmail] = useState<string>('');
    const [countryCode, setCountryCode] = useState<string>('+420');
    const [phone, setPhone] = useState<string>('');
    const [adults, setAdults] = useState<number>(initialState?.adults || 1);
    const [children, setChildren] = useState<number>(initialState?.children || 0);
    const [needsCrib, setNeedsCrib] = useState<boolean>(false);
    const [needsExtraBed, setNeedsExtraBed] = useState<boolean>(false);
    const [paymentMethod, setPaymentMethod] = useState<'transfer' | 'cash' | null>(null);
    const [note, setNote] = useState<string>('');
    const [error, setError] = useState<string>('');
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(true);

    const [startDate, endDate] = dates;
    const totalPeople = adults + children;

    useEffect(() => {
        const loadAvailability = async () => {
            setIsLoading(true);
            try {
                const data = await fetchIcalAvailability();
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                const parsed = ical.parse(data);
                // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                const vcalendar = new ical.Component(parsed);
                const vevents = vcalendar.getAllSubcomponents('vevent');
                const set = new Set<string>();

                vevents.forEach(vevent => {
                    try {
                        const event = new ical.Event(vevent);
                        const start = event.startDate?.toJSDate?.();
                        const end = event.endDate?.toJSDate?.();
                        if (!start || !end || end <= start) return;

                        const dayStart = new Date(start.getFullYear(), start.getMonth(), start.getDate());
                        const dayEnd = new Date(end.getFullYear(), end.getMonth(), end.getDate());
                        const cursor = new Date(dayStart);
                        // Blokujeme i den odjezdu, aby nedošlo k novému příjezdu ve stejný den
                        while (cursor <= dayEnd) {
                            set.add(formatLocalDate(new Date(cursor)));
                            cursor.setDate(cursor.getDate() + 1);
                        }
                    } catch (e) {
                        if (import.meta.env.DEV) {
                            // eslint-disable-next-line no-console
                            console.warn('Chyba při parsování události iCal:', e);
                        }
                    }
                });

                setBlockedDates(set);
            } catch (err) {
                if (import.meta.env.DEV) {
                    // eslint-disable-next-line no-console
                    console.error('Chyba při načítání iCal:', err);
                }
                setError(t('occupancy.error'));
            } finally {
                setIsLoading(false);
            }
        };

        void loadAvailability();
    }, [t]);

    const validateReservation = (nights: number, season: Season) => {
        if (!startDate || !endDate) throw new Error(t('reservation.errors.missingDates'));
        if (!firstName.trim() || !lastName.trim()) throw new Error(t('reservation.errors.missingName'));
        if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) throw new Error(t('reservation.errors.invalidEmail'));
        if (!phone.trim() || !/^\d{9,12}$/.test(phone)) throw new Error(t('reservation.errors.invalidPhone'));
        if (totalPeople < 1) throw new Error(t('reservation.errors.minOnePerson'));
        if (totalPeople > MAX_PEOPLE) throw new Error(t('reservation.errors.maxPeople', { max: MAX_PEOPLE }));
        if (season === 'vanoce' && totalPeople < 5) throw new Error(t('reservation.errors.minPeopleChristmas'));
        if (!paymentMethod) throw new Error(t('reservation.errors.choosePayment'));

        let minNights = 3;
        if (season === 'vanoce') minNights = 7;
        else if (season.startsWith('zimni')) minNights = 5;

        if (nights < minNights) throw new Error(t('reservation.errors.minNights', { nights: minNights }));

        for (let d = new Date(startDate); d < endDate; d.setDate(d.getDate() + 1)) {
            if (blockedDates.has(formatLocalDate(d))) {
                throw new Error(t('reservation.errors.containsBlockedDays'));
            }
        }
    };

    const submitReservation = async (totalPrice: number, nights: number, season: Season): Promise<Reservation | null> => {
        if (isSubmitting) return null;
        setError('');
        setIsSubmitting(true);

        try {
            validateReservation(nights, season);

            const payload: Reservation = {
                roomId: 0,
                checkInDate: formatLocalDate(startDate!),
                checkInTime: arrivalTime,
                checkOutDate: formatLocalDate(endDate!),
                checkOutTime: '09:00',
                note,
                isConfirmed: false,
                email,
                name: `${firstName.trim()} ${lastName.trim()}`,
                phone: `${countryCode}${phone.trim()}`,
                adults,
                children,
                needsCrib,
                needsExtraBed,
                nights,
                totalPrice,
                paymentMethod: paymentMethod as 'transfer' | 'cash',
            };

            const reservation = await createReservation(payload);
            return reservation;
        } catch (err: unknown) {
            let message = t('reservation.errors.unknown');

            if (err instanceof Error) {
                message = err.message;
            }

            if (axios.isAxiosError(err)) {
                if (err.response?.status === 404) {
                    message = t('reservation.errors.serverNotFound');
                } else if (err.response?.status && err.response.status >= 500) {
                    message = t('reservation.errors.serverError');
                } else if (!navigator.onLine) {
                    message = t('reservation.errors.networkError');
                }
            }

            setError(message);
            return null;
        } finally {
            setIsSubmitting(false);
        }
    };

    return {
        dates,
        setDates,
        blockedDates,
        arrivalTime,
        setArrivalTime,
        firstName,
        setFirstName,
        lastName,
        setLastName,
        email,
        setEmail,
        countryCode,
        setCountryCode,
        phone,
        setPhone,
        adults,
        setAdults,
        children,
        setChildren,
        needsCrib,
        setNeedsCrib,
        needsExtraBed,
        setNeedsExtraBed,
        paymentMethod,
        setPaymentMethod,
        note,
        setNote,
        error,
        isSubmitting,
        isLoading,
        submitReservation,
        startDate,
        endDate,
        totalPeople,
        MAX_PEOPLE,
        formatLocalDate
    };
};
